import type { EventBus } from '../core/events';
import type { Rng } from '../core/rng';
import { MATE_LINES, type MateMoment } from '../content/lines/mate';

const MIN_GAP_SECONDS = 2.5;

/** Turns game events into mate (conductor) shouts, throttled so he doesn't talk over himself. */
export function bindMateCalls(events: EventBus, rng: Rng): () => void {
  let lastSaid = -Infinity;
  let boardingCalledFor = '';
  let dropCalledFor = '';

  const say = (moment: MateMoment, stopName = '', force = false): void => {
    const now = performance.now() / 1000;
    if (!force && now - lastSaid < MIN_GAP_SECONDS) return;
    lastSaid = now;
    const text = rng.pick(MATE_LINES[moment]).replaceAll('{stop}', stopName);
    events.emit('mate:say', { text });
  };

  const offs = [
    events.on('stop:approaching', (e) => say('stopAhead', e.stopName)),
    events.on('stop:rated', (e) => {
      if (e.rating === 'perfect') say('perfectStop', e.stopName, true);
      if (e.rating === 'overshot') say('overshot', e.stopName, true);
    }),
    events.on('passenger:boarded', (e) => {
      if (boardingCalledFor === e.stopName) return;
      boardingCalledFor = e.stopName;
      say('boarding', e.stopName);
    }),
    events.on('passenger:dropped', (e) => {
      if (dropCalledFor === e.stopName) return;
      dropCalledFor = e.stopName;
      say('drop', e.stopName);
    }),
    events.on('passenger:missed', (e) => say('missedDrop', e.stopName, true)),
    events.on('run:stumbled', () => say('stumble', '', true)),
    events.on('run:collision', (e) => say(e.rearEnd ? 'rearEnd' : 'crash', '', true)),
    events.on('run:brokeDown', () => say('brokeDown', '', true)),
    events.on('junction:pedestrianFine', () => say('pedestrian', '', true)),
    events.on('junction:approaching', (e) => {
      if (e.signal !== 'green') say('redAhead');
    }),
    events.on('junction:ranRed', () => say('ranRed', '', true)),
    events.on('fuel:low', () => say('fuelLow', '', true)),
    events.on('fuel:empty', () => say('fuelEmpty', '', true)),
    events.on('fuel:full', () => say('fuelFull', '', true)),
    events.on('checkpoint:ahead', () => say('checkpointAhead', '', true)),
    events.on('checkpoint:ran', () => say('checkpointRan', '', true)),
    events.on('checkpoint:dashAsk', () => say('dashAsk', '', true)),
    events.on('checkpoint:dashRefused', () => say('dashRefused', '', true)),
    events.on('checkpoint:cleared', (e) => say(e.dashed ? 'dashPaid' : e.papersProblem ? 'checkpointPapers' : 'checkpointOk', '', true)),
    events.on('trip:started', (e) => {
      if (e.trip > 1) say('uTurn', e.from, true);
    }),
  ];
  return () => offs.forEach((off) => off());
}
